import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ScanLine, 
  ArrowLeft, 
  Upload,
  FileImage,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Package,
  Trash2,
  Eye,
  Truck,
  Zap,
  ShoppingCart,
  XCircle,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import ScanTabs from "@/components/purchasing/ScanTabs";
import { dualPriceText } from "@/components/DualPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScanInvoicePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Edit mode
  const [editedItems, setEditedItems] = useState([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // Validation stats
  const [validationStats, setValidationStats] = useState({
    total: 0,
    totalQty: 0,
    inSystem: 0,
    notInSystem: 0,
    ordered: 0,
    notOrdered: 0
  });

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    // Validate file type - now including PDF
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Endast JPEG, PNG, WEBP eller PDF stöds");
      return;
    }
    
    setFile(selectedFile);
    setIsPdf(selectedFile.type === 'application/pdf');
    
    // Create preview for images, show PDF icon for PDFs
    if (selectedFile.type === 'application/pdf') {
      setPreview(null);
    } else {
      setPreview(URL.createObjectURL(selectedFile));
    }
    
    setResult(null);
    setError(null);
    setEditedItems([]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(droppedFile.type)) {
        toast.error("Endast JPEG, PNG, WEBP eller PDF stöds");
        return;
      }
      setFile(droppedFile);
      setIsPdf(droppedFile.type === 'application/pdf');
      
      if (droppedFile.type === 'application/pdf') {
        setPreview(null);
      } else {
        setPreview(URL.createObjectURL(droppedFile));
      }
      
      setResult(null);
      setError(null);
      setEditedItems([]);
    }
  };

  const handleScan = async () => {
    if (!file) {
      toast.error("Välj en fil först");
      return;
    }
    
    setScanning(true);
    setError(null);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      // Step 1: Scan invoice with OCR
      const response = await axios.post(`${API}/purchasing/ocr/scan-invoice`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (!response.data.success) {
        setError(response.data.error || "Kunde inte tolka fakturan");
        toast.error("Kunde inte tolka fakturan");
        setScanning(false);
        return;
      }
      
      setResult(response.data);
      const scannedItems = response.data.items || [];
      
      // Step 2: Fetch all purchase orders (for matching)
      let allOrders = [];
      try {
        const ordersRes = await axios.get(`${API}/purchasing/orders?limit=500`);
        allOrders = ordersRes.data?.orders || ordersRes.data || [];
      } catch (e) {
        console.error("Could not fetch orders:", e);
      }
      
      // --- Robust matching helpers ---
      // EAN: compare digits only (handles spaces / GTIN-in-artikelnummer / leading zeros)
      const normEan = (v) => (v == null ? "" : String(v)).replace(/\D/g, "");
      // Article/model: trim, uppercase, strip everything except letters+digits
      const normArt = (v) => (v == null ? "" : String(v)).toUpperCase().replace(/[^A-Z0-9]/g, "");
      const isGtin = (v) => { const d = normEan(v); return d.length >= 12 && d.length <= 14; };
      // Extract candidate model/article tokens from a free-text product name
      // (supplier invoices embed the model number in the description, e.g.
      //  "NRK6202AXL4 LODÓWKA GORENJE" -> "NRK6202AXL4").
      const modelTokens = (name) => {
        if (!name) return [];
        return String(name)
          .split(/[\s,;/]+/)
          .map((t) => normArt(t))
          .filter((t) => t.length >= 4 && /[0-9]/.test(t) && /[A-Z]/.test(t));
      };

      // Build maps of pending order items by normalized EAN and artikelnummer.
      const orderItemsByEan = {};
      const orderItemsByArt = {};
      const pushKey = (map, key, val) => { if (!key) return; if (!map[key]) map[key] = []; map[key].push(val); };

      for (const order of allOrders) {
        if (order.status === 'cancelled' || order.status === 'received') continue;

        for (const item of order.items || []) {
          const pendingQty = item.quantity - (item.received_quantity || 0);
          if (pendingQty <= 0) continue;

          const orderInfo = {
            order_id: order.id,
            order_number: order.order_number,
            order_status: order.status,
            pending_qty: pendingQty,
            supplier_name: order.supplier_name,
            artikelnummer: item.artikelnummer,
            ean: item.ean,
          };

          // EAN index: from the item's EAN, and from a GTIN-looking artikelnummer
          pushKey(orderItemsByEan, normEan(item.ean), orderInfo);
          if (isGtin(item.artikelnummer)) pushKey(orderItemsByEan, normEan(item.artikelnummer), orderInfo);
          // Article index: internal artikelnummer (the model number)
          pushKey(orderItemsByArt, normArt(item.artikelnummer), orderInfo);
        }
      }

      const findOrderMatch = (item) => {
        // 1) EAN candidates: invoice ean, system ean, and a GTIN-looking artikelnummer
        const eanKeys = [item.ean, item.system_ean, isGtin(item.artikelnummer) ? item.artikelnummer : null]
          .map(normEan).filter(Boolean);
        for (const k of eanKeys) if (orderItemsByEan[k]) return orderItemsByEan[k];
        // 2) Article/model candidates: invoice artikelnummer, system artikelnummer,
        //    and model tokens pulled from the product description.
        const artKeys = [
          normArt(item.artikelnummer),
          normArt(item.system_artikelnummer),
          ...modelTokens(item.produktnamn),
          ...modelTokens(item.system_produktnamn),
        ].filter(Boolean);
        for (const k of artKeys) if (orderItemsByArt[k]) return orderItemsByArt[k];
        return null;
      };

      // Step 3: Validate each scanned item against inventory and orders
      let inSystemCount = 0;
      let notInSystemCount = 0;
      let orderedCount = 0;
      let notOrderedCount = 0;
      
      const validatedItems = await Promise.all(scannedItems.map(async (item) => {
        const validated = { ...item };
        
        // Search in inventory by EAN (digits), a GTIN-looking artikelnummer,
        // the artikelnummer itself, then model tokens from the description.
        let inventoryMatch = null;
        const eanQueries = [item.ean, isGtin(item.artikelnummer) ? item.artikelnummer : null]
          .map(normEan).filter(Boolean);
        for (const q of eanQueries) {
          try {
            const invRes = await axios.get(`${API}/inventory`, { params: { search: q, limit: 5 } });
            const matches = invRes.data?.items || invRes.data || [];
            if (matches.length > 0) {
              inventoryMatch = matches.find(m => normEan(m.ean) === q) || matches[0];
              if (inventoryMatch) break;
            }
          } catch (e) { /* ignore */ }
        }

        if (!inventoryMatch) {
          const artQueries = [item.artikelnummer, ...modelTokens(item.produktnamn)].filter(Boolean);
          for (const q of artQueries) {
            try {
              const invRes = await axios.get(`${API}/inventory`, { params: { search: q, limit: 5 } });
              const matches = invRes.data?.items || invRes.data || [];
              if (matches.length > 0) {
                const qn = normArt(q);
                inventoryMatch = matches.find(m => normArt(m.artikelnummer) === qn) ||
                                matches.find(m => normArt(m.artikelnummer).includes(qn)) ||
                                matches[0];
                if (inventoryMatch) break;
              }
            } catch (e) { /* ignore */ }
          }
        }
        
        if (inventoryMatch) {
          validated.in_system = true;
          validated.system_produktnamn = inventoryMatch.produktnamn;
          validated.system_ean = inventoryMatch.ean;
          validated.system_artikelnummer = inventoryMatch.artikelnummer;
          // Fill in missing data from system
          if (!validated.ean) validated.ean = inventoryMatch.ean;
          if (!validated.artikelnummer) validated.artikelnummer = inventoryMatch.artikelnummer;
          inSystemCount++;
        } else {
          validated.in_system = false;
          notInSystemCount++;
        }
        
        // Check if product is in a pending order (robust EAN + model matching)
        const orderMatch = findOrderMatch(validated);
        
        if (orderMatch && orderMatch.length > 0) {
          validated.is_ordered = true;
          validated.order_info = orderMatch[0]; // Take first matching order
          orderedCount++;
        } else {
          validated.is_ordered = false;
          notOrderedCount++;
        }
        
        // Price logic: Invoice price first, then system price
        if (!validated.unit_price || validated.unit_price === 0) {
          // Try to get price from supplier_prices
          if (validated.ean || validated.system_ean) {
            try {
              const priceRes = await axios.get(`${API}/purchasing/prices/by-ean/${validated.ean || validated.system_ean}`);
              if (priceRes.data?.purchase_price) {
                validated.unit_price = priceRes.data.purchase_price;
                validated.price_source = "system";
              }
            } catch (e) { /* ignore */ }
          }
        } else {
          validated.price_source = "invoice";
        }
        
        return validated;
      }));
      
      setEditedItems(validatedItems);
      
      // Update stats
      const totalQty = validatedItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
      setValidationStats({
        total: validatedItems.length,
        totalQty,
        inSystem: inSystemCount,
        notInSystem: notInSystemCount,
        ordered: orderedCount,
        notOrdered: notOrderedCount
      });
      
      // Show summary
      if (notInSystemCount > 0 || notOrderedCount > 0) {
        toast.warning(`Hittade ${validatedItems.length} produkter - ${notInSystemCount} saknas i systemet, ${notOrderedCount} är ej beställda`);
      } else {
        toast.success(`Hittade ${validatedItems.length} produkter (${totalQty} st) - alla finns i systemet och är beställda!`);
      }
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Skanningsfel";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setScanning(false);
    }
  };

  const updateItem = (index, field, value) => {
    setEditedItems(prev => prev.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Re-search for product when user changes artikelnummer or EAN
  const reSearchProduct = async (index, searchValue) => {
    if (!searchValue || searchValue.trim() === "") {
      toast.error("Ange artikelnummer eller EAN att söka efter");
      return;
    }
    
    const value = searchValue.trim();
    toast.info(`Söker efter ${value}...`);
    
    try {
      // Search in inventory using the correct endpoint
      let inventoryMatch = null;
      try {
        const invRes = await axios.get(`${API}/inventory`, { params: { search: value, limit: 10 } });
        const matches = invRes.data?.items || [];
        
        // Find exact match by EAN or artikelnummer
        inventoryMatch = matches.find(m => m.ean === value || m.artikelnummer === value);
        if (!inventoryMatch && matches.length > 0) {
          // Try partial match
          inventoryMatch = matches.find(m => 
            (m.artikelnummer && m.artikelnummer.includes(value)) ||
            (m.ean && m.ean.includes(value))
          );
        }
      } catch (e) {
        console.log("Inventory search error:", e);
      }
      
      // Fetch purchase orders
      let orderMatch = null;
      try {
        const ordersRes = await axios.get(`${API}/purchasing/orders?limit=500`);
        const allOrders = ordersRes.data?.orders || ordersRes.data || [];
        
        // Search for matching order item
        for (const order of allOrders) {
          if (order.status === 'cancelled' || order.status === 'received') continue;
          
          for (const item of order.items || []) {
            const pendingQty = item.quantity - (item.received_quantity || 0);
            if (pendingQty <= 0) continue;
            
            const matchByEan = item.ean && item.ean === value;
            const matchByArt = item.artikelnummer && item.artikelnummer === value;
            const partialMatch = item.artikelnummer && item.artikelnummer.includes(value);
            
            if (matchByEan || matchByArt || partialMatch) {
              orderMatch = {
                order_id: order.id,
                order_number: order.order_number,
                order_status: order.status,
                pending_qty: pendingQty,
                supplier_name: order.supplier_name,
                ean: item.ean,
                artikelnummer: item.artikelnummer
              };
              break;
            }
          }
          if (orderMatch) break;
        }
      } catch (e) {
        console.log("Orders search error:", e);
      }
      
      // Update the item with new search results
      setEditedItems(prev => {
        const newItems = prev.map((item, i) => {
          if (i === index) {
            const updated = { ...item };
            
            if (inventoryMatch) {
              updated.in_system = true;
              updated.system_produktnamn = inventoryMatch.produktnamn;
              if (inventoryMatch.ean) updated.ean = inventoryMatch.ean;
              if (inventoryMatch.artikelnummer) updated.artikelnummer = inventoryMatch.artikelnummer;
            } else {
              updated.in_system = false;
              updated.system_produktnamn = null;
            }
            
            if (orderMatch) {
              updated.is_ordered = true;
              updated.order_info = orderMatch;
              // Fill in EAN/artikelnummer from order if found
              if (orderMatch.ean) updated.ean = orderMatch.ean;
              if (orderMatch.artikelnummer) updated.artikelnummer = orderMatch.artikelnummer;
            } else {
              updated.is_ordered = false;
              updated.order_info = null;
            }
            
            return updated;
          }
          return item;
        });
        
        // Update stats
        const inSystem = newItems.filter(i => i.in_system).length;
        const ordered = newItems.filter(i => i.is_ordered).length;
        const totalQty = newItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
        
        setValidationStats({
          total: newItems.length,
          totalQty,
          inSystem,
          notInSystem: newItems.length - inSystem,
          ordered,
          notOrdered: newItems.length - ordered
        });
        
        return newItems;
      });
      
      // Show result
      if (orderMatch) {
        toast.success(`Hittade beställning från ${orderMatch.supplier_name}!`);
      } else if (inventoryMatch) {
        toast.info(`Hittade i lager men inte beställd`);
      } else {
        toast.warning(`Ingen träff för "${value}"`);
      }
      
    } catch (err) {
      console.error("Error re-searching product:", err);
      toast.error("Sökning misslyckades");
    }
  };
  
  // Update validation stats based on current items
  const updateValidationStats = () => {
    // This function is no longer needed as we update inline
  };

  const removeItem = (index) => {
    setEditedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuickReceive = () => {
    // Check if there are items to receive
    if (!editedItems || editedItems.length === 0) {
      toast.error("Inga produkter att ta emot. Skanna en faktura först.");
      return;
    }
    
    // Store scanned items for quick receive flow
    sessionStorage.setItem('quickReceiveItems', JSON.stringify(editedItems));
    sessionStorage.setItem('quickReceiveInfo', JSON.stringify({
      supplier_info: result?.supplier_info,
      matched_supplier: result?.matched_supplier,
      invoice_number: result?.invoice_number,
      invoice_date: result?.invoice_date,
      total_amount: result?.total_amount
    }));
    navigate('/purchasing/quick-receive');
  };

  // Create purchase order from scanned items
  const handleCreateOrder = async () => {
    // Check if there are items to order
    if (!editedItems || editedItems.length === 0) {
      toast.error("Inga produkter att beställa. Skanna en faktura först.");
      return;
    }
    
    // Filter items that are not already ordered
    const itemsToOrder = editedItems.filter(item => !item.is_ordered);
    
    if (itemsToOrder.length === 0) {
      toast.info("Alla produkter är redan beställda!");
      return;
    }
    
    // Store scanned items for order creation flow
    sessionStorage.setItem('createOrderItems', JSON.stringify(itemsToOrder));
    sessionStorage.setItem('createOrderInfo', JSON.stringify({
      supplier_info: result?.supplier_info,
      matched_supplier: result?.matched_supplier,
      invoice_number: result?.invoice_number,
      invoice_date: result?.invoice_date,
      total_amount: result?.total_amount
    }));
    navigate('/purchasing/orders/new?from=scan');
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/purchasing">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <ScanLine className="h-5 w-5 text-primary" />
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Skanna faktura
                </h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Ladda upp en bild på faktura eller följesedel
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Sub-tabs: scanning vs received orders */}
        <ScanTabs />

        {/* Upload Area */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase">Ladda upp fil</CardTitle>
            <CardDescription>
              Stöder JPEG, PNG, WEBP och PDF. AI:n extraherar produkter, priser och kvantiteter.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${(preview || isPdf) ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {file ? (
                <div className="space-y-4">
                  {isPdf ? (
                    <div className="flex flex-col items-center">
                      <FileText className="h-20 w-20 text-red-500" />
                      <Badge className="mt-2 bg-red-500">PDF</Badge>
                    </div>
                  ) : (
                    <img 
                      src={preview} 
                      alt="Förhandsvisning" 
                      className="max-h-64 mx-auto rounded-md shadow-md cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPreviewDialog(true);
                      }}
                    />
                  )}
                  <p className="text-sm text-muted-foreground">{file?.name}</p>
                  <Button 
                    variant="outline" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setPreview(null);
                      setIsPdf(false);
                      setResult(null);
                      setEditedItems([]);
                    }}
                  >
                    Välj annan fil
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-center gap-4">
                    <FileImage className="h-12 w-12 text-muted-foreground/50" />
                    <FileText className="h-12 w-12 text-muted-foreground/50" />
                  </div>
                  <div>
                    <p className="text-lg font-medium">Dra och släpp en fil här</p>
                    <p className="text-sm text-muted-foreground">JPEG, PNG, WEBP eller PDF</p>
                  </div>
                </div>
              )}
            </div>
            
            {file && !result && (
              <div className="mt-4 flex justify-center">
                <Button 
                  onClick={handleScan} 
                  disabled={scanning}
                  size="lg"
                  className="gap-2"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {isPdf ? 'Konverterar PDF och analyserar...' : 'Analyserar med AI...'}
                    </>
                  ) : (
                    <>
                      <ScanLine className="h-5 w-5" />
                      Skanna faktura
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <Card className="border-destructive bg-destructive/10">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && (
          <>
            {/* Invoice Info */}
            {(result.supplier_info || result.invoice_number) && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading uppercase text-base">Fakturainformation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {result.supplier_info?.company_name && (
                      <div>
                        <p className="text-muted-foreground">Leverantör</p>
                        <p className="font-medium">{result.supplier_info.company_name}</p>
                        {result.matched_supplier && (
                          <Badge className="mt-1 bg-green-500 text-white">
                            Matchad: {result.matched_supplier.company_name}
                          </Badge>
                        )}
                      </div>
                    )}
                    {result.invoice_number && (
                      <div>
                        <p className="text-muted-foreground">Fakturanummer</p>
                        <p className="font-medium">{result.invoice_number}</p>
                      </div>
                    )}
                    {result.invoice_date && (
                      <div>
                        <p className="text-muted-foreground">Fakturadatum</p>
                        <p className="font-medium">{result.invoice_date}</p>
                      </div>
                    )}
                    {result.total_amount && (
                      <div>
                        <p className="text-muted-foreground">Totalt</p>
                        <p className="font-medium">
                          {result.currency === 'EUR'
                            ? dualPriceText(result.total_amount, 'EUR')
                            : `${result.total_amount} ${result.currency || 'SEK'}`}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Validation Summary */}
            <Card className="border-2">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold">{validationStats.total}</p>
                    <p className="text-xs text-muted-foreground">Produktrader</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{validationStats.totalQty}</p>
                    <p className="text-xs text-muted-foreground">Totalt antal</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-500">{validationStats.inSystem}</p>
                    <p className="text-xs text-muted-foreground">I systemet</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-500">{validationStats.ordered}</p>
                    <p className="text-xs text-muted-foreground">Beställda</p>
                  </div>
                  <div>
                    {validationStats.notOrdered > 0 ? (
                      <p className="text-2xl font-bold text-amber-500">{validationStats.notOrdered}</p>
                    ) : (
                      <p className="text-2xl font-bold text-green-500">0</p>
                    )}
                    <p className="text-xs text-muted-foreground">Ej beställda</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Extracted Items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading uppercase">
                    Produkter från faktura
                  </CardTitle>
                </div>
                <CardDescription>
                  Granska produkterna - <span className="text-green-500">gröna</span> finns i systemet, <span className="text-blue-500">blåa</span> är beställda
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {editedItems.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Inga produkter hittades.
                  </p>
                ) : (
                  editedItems.map((item, index) => (
                    <Card 
                      key={index} 
                      className={`border-2 ${
                        item.is_ordered ? 'border-green-500/50 bg-green-500/5' :
                        'border-amber-500/50 bg-amber-500/5'
                      }`}
                    >
                      <CardContent className="p-3">
                        {/* Status badges - simplified */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {item.is_ordered ? (
                            <Badge variant="outline" className="text-green-600 border-green-500 bg-green-500/10">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Beställd från {item.order_info?.supplier_name}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-500 bg-amber-500/10">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Ej beställd
                            </Badge>
                          )}
                          
                          {!item.in_system && !item.is_ordered && (
                            <Badge variant="outline" className="text-red-600 border-red-500 bg-red-500/10">
                              <XCircle className="h-3 w-3 mr-1" />
                              Okänd produkt
                            </Badge>
                          )}
                          
                          {item.price_source === "system" && (
                            <Badge variant="secondary" className="text-xs">
                              Systempris
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-12 gap-2 items-end">
                          {/* Artikelnummer */}
                          <div className="col-span-3 space-y-1">
                            <Label className="text-xs">Art.nr</Label>
                            <Input
                              value={item.artikelnummer || ""}
                              onChange={(e) => updateItem(index, "artikelnummer", e.target.value)}
                              onBlur={(e) => !item.is_ordered && reSearchProduct(index, e.target.value)}
                              placeholder="Artikelnummer"
                              className={`text-sm font-mono ${!item.is_ordered ? 'border-amber-500' : ''}`}
                            />
                          </div>
                          
                          {/* EAN */}
                          <div className="col-span-3 space-y-1">
                            <Label className="text-xs">EAN</Label>
                            <Input
                              value={item.ean || ""}
                              onChange={(e) => updateItem(index, "ean", e.target.value)}
                              onBlur={(e) => !item.is_ordered && reSearchProduct(index, e.target.value)}
                              placeholder="EAN-nummer"
                              className={`font-mono text-sm ${!item.is_ordered ? 'border-amber-500' : ''}`}
                            />
                          </div>
                          
                          {/* Search button for non-ordered items */}
                          {!item.is_ordered && (
                            <div className="col-span-1 flex items-end">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-9 w-9"
                                onClick={() => reSearchProduct(index, item.artikelnummer || item.ean)}
                                title="Sök efter produkt"
                              >
                                <Search className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          
                          {/* Quantity */}
                          <div className={`space-y-1 ${!item.is_ordered ? 'col-span-1' : 'col-span-2'}`}>
                            <Label className="text-xs">Antal</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity || 1}
                              onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                              className="text-sm text-center"
                            />
                          </div>
                          
                          {/* Price */}
                          <div className="col-span-3 space-y-1">
                            <Label className="text-xs">Pris ({item.currency || 'SEK'})</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_price || 0}
                              onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                              className="text-sm text-right"
                            />
                          </div>
                          
                          {/* Actions */}
                          <div className="col-span-1 flex justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => removeItem(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Product name from system */}
                        {item.in_system && item.system_produktnamn && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {item.system_produktnamn}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            {editedItems.length > 0 && (
              <Card className="bg-primary/5 border-primary/30">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <p className="font-medium">
                        {validationStats.ordered} av {validationStats.total} produkter är beställda och redo att tas emot
                      </p>
                      {validationStats.notOrdered > 0 && (
                        <p className="text-sm text-amber-600">
                          ⚠️ {validationStats.notOrdered} produkt(er) är inte beställda
                        </p>
                      )}
                      {validationStats.notInSystem > 0 && (
                        <p className="text-sm text-red-600">
                          ⚠️ {validationStats.notInSystem} produkt(er) finns inte i systemet
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                      {validationStats.notOrdered > 0 && (
                        <Button onClick={handleCreateOrder} variant="outline" className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-500/10">
                          <ShoppingCart className="h-4 w-4" />
                          Skapa inköpsorder ({validationStats.notOrdered} st)
                        </Button>
                      )}
                      <Button onClick={handleQuickReceive} className="gap-2 bg-green-600 hover:bg-green-700">
                        <Zap className="h-4 w-4" />
                        Ta emot produkter
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Image Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Förhandsvisning</DialogTitle>
          </DialogHeader>
          {preview && (
            <img 
              src={preview} 
              alt="Förhandsvisning" 
              className="w-full rounded-md"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
